import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useCanManage, useCanManageAnyLocation, type UserRole } from '../store/auth';

interface Props {
  children: ReactNode;
  /**
   * Wenn gesetzt, wird zusätzlich die Rolle geprüft. Kann eine einzelne Rolle
   * oder ein Array von erlaubten Rollen sein.
   */
  requireRole?: UserRole | UserRole[];
  /** Wenn true: Zugriff nur für Admin oder Owner/Alt-Manager der AKTIVEN Firma (Chef-Ebene). */
  requireManage?: boolean;
  /**
   * Wenn true: Zugriff für Admin, Owner ODER Filialleitung an mind. einem
   * Standort der aktiven Firma (Kapitel 1.3) — für Seiten, die selbst weiter
   * auf den jeweiligen Standort scopen (Mitarbeiter-Verwaltung, Wochenplan).
   * Schwächer als `requireManage`; beide zusammen ergeben keinen Sinn.
   */
  requireManageAnyLocation?: boolean;
}

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  superadmin: 'Admin',
  manager: 'Filialleitung',
  employee: 'Mitarbeiter',
  user: 'Mitarbeiter',
};

export function ProtectedRoute({ children, requireRole, requireManage, requireManageAnyLocation }: Props) {
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const canManage = useCanManage();
  const canManageAnyLocation = useCanManageAnyLocation();
  const location = useLocation();

  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Kapitel 3.2 (Screen 7): solange kein eigenes Passwort festgelegt ist,
  // führt jeder geschützte Weg auf „Passwort festlegen" (der Server blockt
  // die übrige API in diesem Zustand ohnehin).
  if (user.passwordIsInitial) {
    return <Navigate to="/passwort-festlegen" replace />;
  }

  if (requireManage && !canManage) {
    return (
      <div className="text-center mt-12 text-slate-300">
        <p className="text-3xl mb-2">403</p>
        <p>Diese Seite ist nur für die Firmen-Leitung (Chef) oder den Admin zugänglich.</p>
      </div>
    );
  }

  if (requireManageAnyLocation && !canManageAnyLocation) {
    return (
      <div className="text-center mt-12 text-slate-300">
        <p className="text-3xl mb-2">403</p>
        <p>Diese Seite ist nur für Chef, Filialleitung oder Admin zugänglich.</p>
      </div>
    );
  }

  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!allowed.includes(user.role)) {
      const labels = allowed.map((r) => ROLE_LABEL[r]).join(' oder ');
      return (
        <div className="text-center mt-12 text-slate-300">
          <p className="text-3xl mb-2">403</p>
          <p>Diese Seite ist nur für {labels} zugänglich.</p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
